import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        service: formData.service,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4nec9d0qurnbdguf2p0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0a0f1c]">
      <div className="grid md:grid-cols-2">
        {/* Left Panel - Dark */}
        <div className="bg-black px-12 py-16 flex flex-col justify-center">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-12">
            LET'S CREATE<br />
            <span className="text-[#00d9ff]">TOGETHER</span>
          </h2>

          <div className="space-y-8">
            {/* Location */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-[#ff6b35] text-2xl"></i>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">LOCATION</div>
                <div className="text-white text-lg">
                  8422 Mandela Section<br />
                  Tumahole, Parys, 9585<br />
                  South Africa
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-[#ff6b35] text-2xl"></i>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">PHONE</div>
                <a href="tel:0817844091" className="text-[#00d9ff] text-lg hover:underline">
                  081 784 4091
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-[#ff6b35] text-2xl"></i>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">EMAIL</div>
                <a href="mailto:masterjaemultimedialap@gmail.com" className="text-[#00d9ff] text-lg hover:underline break-all">
                  masterjaemultimedialap@gmail.com
                </a>
              </div>
            </div>

            {/* Facebook */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <i className="ri-facebook-circle-line text-[#ff6b35] text-2xl"></i>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">SOCIAL MEDIA</div>
                <a href="https://facebook.com/MasterJaeProduction" target="_blank" rel="noopener noreferrer" className="text-[#00d9ff] text-lg hover:underline">
                  Master Jae Production
                </a>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="text-gray-400 text-sm mb-3">BUSINESS HOURS</div>
            <div className="text-white">
              <div className="flex justify-between mb-2">
                <span>Monday - Friday</span>
                <span className="text-[#00d9ff]">9:00 AM - 6:00 PM</span>
              </div>
              <div className="flex justify-between mb-2">
                <span>Saturday</span>
                <span className="text-[#00d9ff]">10:00 AM - 4:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday</span>
                <span className="text-gray-500">Closed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Light */}
        <div className="bg-[#f5f5f5] px-12 py-16">
          <h3 className="text-3xl font-bold text-black mb-8">Send Us a Message</h3>
          
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-[#00d9ff] outline-none py-3 text-black placeholder-gray-500 transition-colors duration-300"
              />
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-[#00d9ff] outline-none py-3 text-black placeholder-gray-500 transition-colors duration-300"
              />
            </div>

            {/* Service Interest */}
            <div>
              <select
                name="service"
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                required
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-[#00d9ff] outline-none py-3 text-black transition-colors duration-300 cursor-pointer"
              >
                <option value="">Select Service Interest</option>
                <option value="music-studio">Music Studio</option>
                <option value="graphic-design">Graphic Design</option>
                <option value="post-production">Post Production</option>
                <option value="multiple-services">Multiple Services</option>
                <option value="consultation">Consultation</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                maxLength={500}
                rows={5}
                className="w-full bg-transparent border-b-2 border-gray-300 focus:border-[#00d9ff] outline-none py-3 text-black placeholder-gray-500 transition-colors duration-300 resize-none"
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.message.length}/500
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#ff6b35] text-white py-4 rounded-lg hover:bg-[#ff8555] transition-all duration-300 font-bold text-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              <span>{isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}</span>
              <i className="ri-arrow-right-line"></i>
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                Thank you! Your message has been sent successfully.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                Sorry, there was an error sending your message. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
